import axios from '../dataClient'
import { useEffect, useRef } from 'react'
import { useAuth } from '../../providers/AuthProvider'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser'
import { getRuntimeEnv } from '../../runtimeEnv'

const REFRESH_TOKEN_PATH = '/user/refresh'

let refreshInFlight = null

function isManualTokenExpiredResponse(response) {
  const status = response?.status
  if (status !== 401 && status !== 403) return false
  const payload = response?.data
  const body = payload?.data && typeof payload.data?.name === 'string' ? payload.data : payload
  return body?.name === 'TokenExpiredError' || (typeof body?.message === 'string' && /jwt expired/i.test(body.message))
}

const useAxiosInterceptors = () => {
  const { authState, setAuthState } = useAuth()
  const { instance, inProgress, accounts } = useMsal()

  const authStateRef = useRef(authState)
  const instanceRef = useRef(instance)
  const inProgressRef = useRef(inProgress)
  const accountsRef = useRef(accounts)
  const setAuthStateRef = useRef(setAuthState)

  authStateRef.current = authState
  instanceRef.current = instance
  inProgressRef.current = inProgress
  accountsRef.current = accounts
  setAuthStateRef.current = setAuthState

  useEffect(() => {
    const reqInterceptor = axios.interceptors.request.use(
      async config => {
        if (config._retryBypass) {
          return config
        }

        const currentAuthState = authStateRef.current
        const currentAccounts = accountsRef.current
        const currentInProgress = inProgressRef.current
        const currentInstance = instanceRef.current

        if (currentAuthState.loginMethod === 'microsoft') {
          if (!currentAccounts || currentAccounts.length === 0) return config

          const accessTokenRequest = {
            scopes: [`api://${getRuntimeEnv('REACT_APP_AZURE_CLIENT_ID')}/access_as_user`],
            account: currentAccounts[0]
          }

          if (currentInProgress === InteractionStatus.None) {
            try {
              const accessTokenResponse = await currentInstance.acquireTokenSilent(accessTokenRequest)
              const accessToken = accessTokenResponse.accessToken
              config.headers['Authorization'] = `Bearer ${accessToken}`
              config.headers['AuthType'] = currentAuthState.loginMethod
            } catch (error) {
              if (error instanceof InteractionRequiredAuthError) {
                currentInstance.acquireTokenRedirect(accessTokenRequest)
              }
            }
          }
        }

        if (currentAuthState.loginMethod === 'manual') {
          const token = localStorage.getItem('accessToken') || currentAuthState?.accessToken
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
            config.headers['AuthType'] = currentAuthState.loginMethod
          }
        }

        const securityToken = localStorage.getItem('securityToken')
        if (!currentAuthState.loginMethod && securityToken) {
          config.headers['Security'] = `Bearer ${securityToken}`
          config.headers['AuthType'] = 'securityToken'
        } else {
          delete config.headers['Security']
        }

        return config
      },
      error => {
        return Promise.reject(error)
      }
    )

    const resInterceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const { response, config: originalRequest } = error

        if (authStateRef.current.loginMethod === 'microsoft') {
          return Promise.reject(error)
        }

        if (originalRequest?._skipTokenRefresh) {
          return Promise.reject(error)
        }

        if (!isManualTokenExpiredResponse(response) || originalRequest?._retry) {
          return Promise.reject(error)
        }

        originalRequest._retry = true
        try {
          if (!refreshInFlight) {
            refreshInFlight = axios
              .get(REFRESH_TOKEN_PATH, { _skipTokenRefresh: true })
              .then(res => {
                refreshInFlight = null
                return res
              })
              .catch(err => {
                refreshInFlight = null
                throw err
              })
          }
          const refreshResponse = await refreshInFlight
          const data = refreshResponse?.data
          const newToken =
            typeof data?.accessToken === 'string' ? data.accessToken : typeof data?.data?.accessToken === 'string' ? data.data.accessToken : undefined
          if (!newToken) {
            throw new Error('Refresh response missing accessToken')
          }

          localStorage.setItem('accessToken', newToken)
          setAuthStateRef.current(prev => ({ ...prev, accessToken: newToken }))
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`

          originalRequest._retryBypass = true
          return axios(originalRequest)
        } catch (e) {
          localStorage.removeItem('accessToken')
          localStorage.removeItem('loginMethod')
          setAuthStateRef.current({ accessToken: null, loginMethod: '', isLoggedIn: false })
          return Promise.reject(e)
        }
      }
    )

    return () => {
      axios.interceptors.request.eject(reqInterceptor)
      axios.interceptors.response.eject(resInterceptor)
    }
  }, [])
}

export default useAxiosInterceptors
